import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./App.css";
import Login from './Login';

const API_URL = window.location.hostname === "localhost"
    ? "http://localhost:8000/api"
    : `${window.location.protocol}//${window.location.hostname}/api`;


// Configuración global de axios
axios.defaults.withCredentials = true;
axios.defaults.baseURL = API_URL;

// Interceptor: renueva el access token automáticamente cuando expira
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(token);
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        original.headers['Authorization'] = `Bearer ${token}`;
        return axios(original);
      });
    }
    original._retry = true;
    isRefreshing = true;
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) {
      isRefreshing = false;
      localStorage.removeItem('access_token');
      window.location.reload();
      return Promise.reject(error);
    }
    try {
      const { data } = await axios.post('/token/refresh/', { refresh });
      localStorage.setItem('access_token', data.access);
      processQueue(null, data.access);
      original.headers['Authorization'] = `Bearer ${data.access}`;
      return axios(original);
    } catch (err) {
      processQueue(err, null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.reload();
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

function App() {
  const [songs, setSongs] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showPlaylistForm, setShowPlaylistForm] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [selectedSongForPlaylist, setSelectedSongForPlaylist] = useState(null);
  const [showPlaylistMenu, setShowPlaylistMenu] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [activeTab, setActiveTab] = useState('albums'); // 'albums' o 'songs'
  const [toastMessage, setToastMessage] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [audioVolume, setAudioVolume] = useState(1); // Volumen de 0 a 1
  const [isMuted, setIsMuted] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(1);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [isShuffle, setIsShuffle] = useState(false);
  const [repeatMode, setRepeatMode] = useState('off'); // 'off', 'all', 'one'
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTargetPlaylist, setShareTargetPlaylist] = useState(null);
  const [shareUsername, setShareUsername] = useState("");
  const [currentUser, setCurrentUser] = useState(null);

 const formatTime = (sec) => {
    if (!sec || isNaN(sec)) return "0:00";
    const minutes = Math.floor(sec / 60);
    const seconds = Math.floor(sec % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };



// Cargar datos iniciales — valida el token antes de mostrar la app
useEffect(() => {
  const token = localStorage.getItem('access_token');
  if (!token) return;
  axios.get(`${API_URL}/user/me/`, { headers: { Authorization: `Bearer ${token}` } })
    .then(res => {
      setCurrentUser(res.data);
      setIsAuthenticated(true);
      fetchSongs();
      fetchPlaylists();
    })
    .catch(() => {
      // El interceptor ya intentó renovar el token y falló — se limpió el storage
    });
}, []);

// Cargar volumen guardado al iniciar
useEffect(() => {
  const savedVolume = localStorage.getItem('spotifyVolume');
  if (savedVolume) {
    const vol = parseFloat(savedVolume);
    setAudioVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = vol;
    }
  }
}, []);

// Actualizar volumen del audio cuando cambia
useEffect(() => {
  if (audioRef.current) {
    audioRef.current.volume = isMuted ? 0 : audioVolume;
  }
}, [audioVolume, isMuted]);

// Función para cambiar volumen
const handleVolumeChange = (e) => {
  const newVolume = parseFloat(e.target.value);
  setAudioVolume(newVolume);
  setIsMuted(newVolume === 0);
  localStorage.setItem('spotifyVolume', newVolume);
  
  // Actualizar CSS variable para el gradiente visual
  if (e.target) {
    e.target.style.setProperty('--volume-percent', newVolume * 100);
  }
};
// Función para mute/unmute
const toggleMute = () => {
  if (isMuted) {
    setIsMuted(false);
    setAudioVolume(previousVolume);
  } else {
    setPreviousVolume(audioVolume);
    setIsMuted(true);
  }
};

// Función para obtener el ícono de volumen
const getVolumeIcon = () => {
  if (isMuted || audioVolume === 0) return "🔇";
  if (audioVolume < 0.3) return "🔈";
  if (audioVolume < 0.7) return "🔉";
  return "🔊";
};

// Función para toggle del slider de volumen (móvil y desktop)
const toggleVolumeSlider = () => {
  setShowVolumeSlider(!showVolumeSlider);
};

// Detectar si es dispositivo móvil
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
};

const isIOS = () => {
  // Detección para iPad moderno (iPadOS 13+)
  const isIPad = /iPad/.test(navigator.userAgent) || 
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  const isIPhone = /iPhone|iPod/.test(navigator.userAgent);
  
  return (isIPad || isIPhone) && !window.MSStream;
};

// Efecto para manejar mouseup/touchend global (para el slider en desktop)
useEffect(() => {
  const handleGlobalMouseUp = () => {
    if (isSeeking && audioRef.current && progressRef.current) {
      const seekValue = parseFloat(progressRef.current.value);
      if (!isNaN(seekValue)) {
        const newTime = (seekValue / 100) * audioRef.current.duration;
        audioRef.current.currentTime = newTime;
        if (isPlaying) {
          audioRef.current.play();
        }
      }
      setIsSeeking(false);
    }
  };

  const handleGlobalTouchEnd = () => {
    if (isSeeking && audioRef.current && progressRef.current) {
      const seekValue = parseFloat(progressRef.current.value);
      if (!isNaN(seekValue)) {
        const newTime = (seekValue / 100) * audioRef.current.duration;
        audioRef.current.currentTime = newTime;
        if (isPlaying) {
          audioRef.current.play();
        }
      }
      setIsSeeking(false);
    }
  };

  document.addEventListener('mouseup', handleGlobalMouseUp);
  document.addEventListener('touchend', handleGlobalTouchEnd);

  return () => {
    document.removeEventListener('mouseup', handleGlobalMouseUp);
    document.removeEventListener('touchend', handleGlobalTouchEnd);
  };
}, [isSeeking, isPlaying]);

//ESTO SE MODIFICO PARA AHORA PODER ENTRAR CON AUTENTIFICACION
  const fetchSongs = async () => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await axios.get(`${API_URL}/songs/`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setSongs(response.data);
  } catch (error) {
    console.error("Error cargando canciones:", error);
  }
};

  const fetchPlaylists = async () => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await axios.get(`${API_URL}/playlists/`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setPlaylists(response.data);
  } catch (error) {
    console.error("Error cargando playlists:", error);
  }
};
  // === FUNCIONES DE REPRODUCCIÓN ===
  const playSong = (song) => {
    if (!song || !song.audio_file) return;
    setCurrentSong(song);
    setIsPlaying(true);
    setTimeout(() => {
      if (audioRef.current) audioRef.current.play();
    }, 100);
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const nextSong = () => {
  if (!currentSong) return;
  
  // Usar canciones de la playlist si está seleccionada, sino todas
  const currentSongs = selectedPlaylist ? selectedPlaylist.songs : songs;
  
  // Si está en modo repeat one, reproducir la misma canción
  if (repeatMode === 'one') {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
    return;
  }

  const currentIndex = currentSongs.findIndex((s) => s.id === currentSong.id);
  let nextIndex;

  if (isShuffle) {
    // Modo shuffle: canción aleatoria (que no sea la actual)
    do {
      nextIndex = Math.floor(Math.random() * currentSongs.length);
    } while (nextIndex === currentIndex && currentSongs.length > 1);
  } else {
    // Modo normal: siguiente canción
    nextIndex = (currentIndex + 1) % currentSongs.length;
  }

  setCurrentSong(currentSongs[nextIndex]);
  setIsPlaying(true);
  setTimeout(() => audioRef.current.play(), 100);
};

const toggleShuffle = () => {
  setIsShuffle(!isShuffle);
  setToastMessage(isShuffle ? "Shuffle desactivado" : "🔀 Shuffle activado");
  setTimeout(() => setToastMessage(""), 1500);
};

const toggleRepeat = () => {
  const modes = ['off', 'all', 'one'];
  const currentIndex = modes.indexOf(repeatMode);
  const nextMode = modes[(currentIndex + 1) % modes.length];
  setRepeatMode(nextMode);
  
  const messages = {
    off: "Repetir desactivado",
    all: "🔁 Repetir todas",
    one: "🔂 Repetir una"
  };
  
  setToastMessage(messages[nextMode]);
  setTimeout(() => setToastMessage(""), 1500);
};


 const prevSong = () => {
  if (!currentSong) return;
  
  // Usar canciones de la playlist si está seleccionada, sino todas
  const currentSongs = selectedPlaylist ? selectedPlaylist.songs : songs;
  
  const currentIndex = currentSongs.findIndex((s) => s.id === currentSong.id);
  const prevIndex = (currentIndex - 1 + currentSongs.length) % currentSongs.length;
  
  setCurrentSong(currentSongs[prevIndex]);
  setIsPlaying(true);
  setTimeout(() => audioRef.current.play(), 100);
};

// === CONTROL DE SLIDER ===
const handleProgress = () => {
  if (audioRef.current && !isSeeking) {
    const time = audioRef.current.currentTime;
    const total = audioRef.current.duration;
    setCurrentTime(time);
    setDuration(total);
    if (progressRef.current)
      progressRef.current.value = (time / total) * 100 || 0;
  }
};

const handleSeekStart = () => {
  setIsSeeking(true);
  if (audioRef.current) {
    audioRef.current.pause();
  }
};

const handleSeekChange = (e) => {
  if (!isSeeking) return;
  const seekValue = parseFloat(e.target.value);
  if (audioRef.current && !isNaN(seekValue)) {
    const newTime = (seekValue / 100) * audioRef.current.duration;
    setCurrentTime(newTime);
  }
};

const handleSeekEnd = () => {
  if (audioRef.current && progressRef.current) {
    const seekValue = parseFloat(progressRef.current.value);
    if (!isNaN(seekValue)) {
      const newTime = (seekValue / 100) * audioRef.current.duration;
      audioRef.current.currentTime = newTime;
      if (isPlaying) {
        audioRef.current.play();
      }
    }
  }
  setIsSeeking(false);
};

  // === PLAYLISTS ===
  const createPlaylist = async () => {
    if (newPlaylistName.trim() === "") return;
    try {
      const token = localStorage.getItem('access_token'); // 🔑 Obtener token
      await axios.post(`${API_URL}/playlists/`, {
        name: newPlaylistName,
        description: "",
        song_ids: [],
      }, {
      headers: {
        'Authorization': `Bearer ${token}` // 🔑 Enviar token
      }
     });
      setNewPlaylistName("");
      setShowPlaylistForm(false);
      fetchPlaylists();
    } catch (error) {
      console.error("Error creando playlist:", error);
    }
  };

const deletePlaylist = async (playlistId, playlistName) => {
  if (!window.confirm(`¿Eliminar la playlist "${playlistName}"?`)) {
    return;
  }

  try {
    const token = localStorage.getItem('access_token'); // 🔑 AGREGAR TOKEN
    
    await axios.delete(`${API_URL}/playlists/${playlistId}/`, {
      headers: {
        'Authorization': `Bearer ${token}` // 🔑 ENVIAR TOKEN
      }
    });

    setToastMessage(`Eliminada: "${playlistName}"`);
    setTimeout(() => setToastMessage(''), 1500);

    // Si estás viendo esa playlist, volver a todas las canciones
    if (selectedPlaylist && selectedPlaylist.id === playlistId) {
      setSelectedPlaylist(null);
    }

    // Refrescar lista de playlists
    await fetchPlaylists();
  } catch (error) {
    console.error("Error al eliminar:", error);
    setToastMessage("❌ Error al eliminar");
    setTimeout(() => setToastMessage(''), 1500);
  }
};

const addSongToPlaylist = async (playlistId, songId, playlistName) => {
  try {
    const token = localStorage.getItem('access_token'); // 🔑 AGREGAR TOKEN
    
    await axios.post(`${API_URL}/playlists/${playlistId}/add_song/`, {
      song_id: songId,
    }, {
      headers: {
        'Authorization': `Bearer ${token}` // 🔑 ENVIAR TOKEN
      }
    });

    setToastMessage(`Agregada a "${playlistName}"`);
    setTimeout(() => setToastMessage(''), 1500);

    // NUEVO: Refrescar playlists
    await fetchPlaylists();

    // NUEVO: Si estás viendo esa playlist, actualízarla
    if (selectedPlaylist && selectedPlaylist.id === playlistId) {
      const response = await axios.get(`${API_URL}/playlists/${playlistId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSelectedPlaylist(response.data);
    }

    setShowPlaylistMenu(false);
    setSelectedSongForPlaylist(null);
  } catch (error) {
    console.error("Error agregando canción:", error);
    setToastMessage("❌ Error al agregar canción");
    setTimeout(() => setToastMessage(''), 1500);
  }
};

const removeSongFromPlaylist = async (playlistId, songId, songTitle) => {
  const confirmRemove = window.confirm(
    `¿Quitar "${songTitle}" de esta playlist?`
  );
  if (!confirmRemove) return;
  
  const token = localStorage.getItem('access_token');

  try {
    await axios.post(`${API_URL}/playlists/${playlistId}/remove_song/`, {
      song_id: songId,
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });

    setToastMessage(`🗑️ Canción eliminada de la playlist`);
    setTimeout(() => setToastMessage(""), 1500);

    // Actualizar la playlist actual
    const response = await axios.get(`${API_URL}/playlists/${playlistId}/`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setSelectedPlaylist(response.data);

    await fetchPlaylists();
  } catch (error) {
    console.error("Error eliminando canción:", error);
    setToastMessage(`❌ Error al eliminar canción`);
    setTimeout(() => setToastMessage(""), 1500);
  }
};
  const openShareModal = (playlist) => {
  setShareTargetPlaylist(playlist);
  setShareUsername("");
  setShowShareModal(true);
};

const sendShare = async () => {
  if (!shareUsername.trim()) return;
  const token = localStorage.getItem('access_token');
  try {
    await axios.post(`${API_URL}/playlists/${shareTargetPlaylist.id}/share/`,
      { username: shareUsername.trim() },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setToastMessage(`Compartida con ${shareUsername.trim()}`);
    setTimeout(() => setToastMessage(""), 2000);
    setShowShareModal(false);
  } catch (error) {
    const msg = error.response?.data?.error || "Error al compartir";
    setToastMessage(`❌ ${msg}`);
    setTimeout(() => setToastMessage(""), 2000);
  }
};

  const filteredSongs = searchQuery
    ? songs.filter(
        (song) =>
          song.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          song.artist_name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : songs;

  // Agrupa las canciones por álbum para la vista de álbumes
  const albums = React.useMemo(() => {
    const map = new Map();
    songs.forEach((song) => {
      const key = song.album ?? 'sin-album';
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          title: song.album_title || 'Sin álbum',
          cover: song.album_cover,
          artistCounts: {},
          songs: [],
        });
      }
      const entry = map.get(key);
      entry.songs.push(song);
      entry.artistCounts[song.artist_name] = (entry.artistCounts[song.artist_name] || 0) + 1;
    });
    return Array.from(map.values()).map((album) => {
      const [mainArtist] = Object.entries(album.artistCounts).sort((a, b) => b[1] - a[1])[0];
      return { ...album, artist_name: mainArtist };
    });
  }, [songs]);

  const filteredAlbums = searchQuery
    ? albums.filter(
        (album) =>
          album.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (album.artist_name &&
            album.artist_name.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : albums;

  const openAlbum = (album) => {
    setSelectedAlbum(album);
    setIsMobileMenuOpen(false);
  };

  const goBackToAllSongs = () => {
    setSelectedPlaylist(null);
    setSelectedAlbum(null);
    fetchSongs();
  };

const handleLogout = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  setIsAuthenticated(false);
  setCurrentSong(null);
  setSongs([]);
  setPlaylists([]);
};

return (
  <>
    {!isAuthenticated ? (
      <Login onLoginSuccess={async () => {
  setIsAuthenticated(true);
  
  // Obtener información del usuario
  try {
    const token = localStorage.getItem('access_token');
    const response = await axios.get(`${API_URL}/user/me/`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setCurrentUser(response.data);
  } catch (error) {
    console.error("Error obteniendo usuario:", error);
  }
  
  fetchSongs();
  fetchPlaylists();
}} />
    ) : (
      <div className="App">

{/* Todo tu código actual va aquí sin cambios */}
<header className="header">

  {/* Botón hamburguesa para móvil */}
  <button
    className="mobile-menu-btn"
    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
  >
    ☰
  </button>

<h1>Spotify Bugatti {currentUser && <span className="user-badge">👤 {currentUser.username}</span>}</h1>
   {/* Botón Logout */}
  <button 
    className="logout-btn"
    onClick={handleLogout}
    title="Cerrar sesión"
  >
    👤 Salir
  </button>

  {/* Búsqueda desktop */}
  <input
    type="text"
    placeholder="Buscar canciones, artistas..."
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    className="search-input desktop-search"
  />

  {/* Botón búsqueda móvil */}
  <button
    className="mobile-search-btn"
    onClick={() => setShowMobileSearch(true)}
  >
    🔍
  </button>
</header>
      <div className="main-content">
        {/* === SIDEBAR === */}
        <aside className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <button
                className="mobile-close-btn"
                onClick={() => setIsMobileMenuOpen(false)}
                >
                Música
        </button>
          <div className="playlist-section">
            <div className="playlist-header">
              <h2>Playlists</h2>
              <button
                onClick={() => setShowPlaylistForm(!showPlaylistForm)}
                className="add-playlist-btn"
              >
                +
              </button>
            </div>

            {showPlaylistForm && (
              <div className="playlist-form">
                <input
                  type="text"
                  placeholder="Nombre de playlist"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                />
                <button onClick={createPlaylist}>Crear</button>
              </div>
            )}

            <ul className="playlist-list">
  {playlists.map((playlist) => (
    <li 
  key={playlist.id} 
  className={`playlist-item ${selectedPlaylist?.id === playlist.id ? "active" : ""}`}
>
      <span 
  onClick={(e) => {
    e.stopPropagation();
    setSelectedPlaylist(playlist);
    setSelectedAlbum(null);
    setIsMobileMenuOpen(false);
  }}
  className="playlist-name"
>
        {playlist.name}
        {playlist.owner_username !== currentUser?.username && (
          <span style={{fontSize:'0.7em', color:'#aaa', marginLeft:'4px'}}>
            ({playlist.owner_username})
          </span>
        )}
      </span>
      
      {playlist.owner_username === currentUser?.username && (
        <button
          className="share-btn"
          onClick={(e) => { e.stopPropagation(); openShareModal(playlist); }}
          title="Compartir playlist"
        >
          📤
        </button>
      )}
      
      {playlist.owner_username === currentUser?.username && (
        <button
          className="delete-btn"
          onClick={(e) => { e.stopPropagation(); deletePlaylist(playlist.id, playlist.name); }}
          title="Eliminar playlist"
        >
          🗑️
        </button>
      )}
    </li>
  ))}
</ul>
          </div>

          {/* === PLAYER === */}
          {currentSong && (
            <div className="sidebar-player">
              <img
                src={currentSong.album_cover}
                alt="cover"
                className="sidebar-cover"
              />
              <h3>{currentSong.title}</h3>
              <p>{currentSong.artist_name}</p>

             <div className="sidebar-controls">
  <button 
    onClick={toggleShuffle}
    className={isShuffle ? "active-control" : ""}
    title={isShuffle ? "Shuffle activado" : "Shuffle desactivado"}
  >
    🔀
  </button>
  <button onClick={prevSong}>⏮</button>
  <button onClick={togglePlayPause}>
    {isPlaying ? "⏸" : "▶"}
  </button>
  <button onClick={nextSong}>⏭</button>
  <button 
    onClick={toggleRepeat}
    className={repeatMode !== 'off' ? "active-control" : ""}
    title={
      repeatMode === 'off' ? "No repetir" : 
      repeatMode === 'all' ? "Repetir todas" : 
      "Repetir una"
    }
  >
    {repeatMode === 'off' ? "🔁" : repeatMode === 'all' ? "🔁" : "🔂"}
  </button>

{/*  Control de volumen */}
{!isIOS() && (
  <div className="volume-control">
    <button
      className="volume-btn"
      onClick={() => {
        if (isMobileDevice()) {
          setShowVolumeSlider(!showVolumeSlider);
        } else {
          toggleMute();
        }
      }}
      onMouseEnter={() => !isMobileDevice() && setShowVolumeSlider(true)}
      onMouseLeave={() => !isMobileDevice() && setShowVolumeSlider(false)}
    >
      {getVolumeIcon()}
    </button>

    {showVolumeSlider && (
      <div
        className="volume-slider-container"
        onMouseEnter={() => !isMobileDevice() && setShowVolumeSlider(true)}
        onMouseLeave={() => !isMobileDevice() && setShowVolumeSlider(false)}
      >
        <input
          type="range"
          className="volume-slider"
          min="0"
          max="1"
          step="0.01"
          value={isMuted ? 0 : audioVolume}
          onChange={handleVolumeChange}
          onInput={handleVolumeChange}
          orient="vertical"
          style={{ WebkitAppearance: 'slider-vertical' }}
        />
      </div>
    )}
  </div>
)}

</div>

<input
  type="range"
  ref={progressRef}
  className="progress-bar"
  min="0"
  max="100"
  step="0.01"
  value={duration ? (currentTime / duration) * 100 : 0}
  onInput={handleSeekChange}
  onChange={handleSeekChange}
  onMouseDown={handleSeekStart}
  onTouchStart={handleSeekStart}
/>

              <div className="time-display">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>

              <audio
                ref={audioRef}
                src={currentSong.audio_file}
                preload="auto"
                autoPlay
                onTimeUpdate={handleProgress}
                onEnded={() => {
    const currentSongs = selectedPlaylist ? selectedPlaylist.songs : songs;
    
    if (repeatMode === 'one') {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    } else if (repeatMode === 'all' || isShuffle) {
      nextSong();
    } else {
      // Solo avanzar si no es la última canción
      const currentIndex = currentSongs.findIndex((s) => s.id === currentSong.id);
      if (currentIndex < currentSongs.length - 1) {
        nextSong();
      } else {
        setIsPlaying(false);
      }
    }
  }}
/>

            </div>
          )}
        </aside>

        {/* === SONG LIST === */}
        <main className="song-list">
          <div className="songs-header">
            {selectedPlaylist ? (
              <>
                <button className="back-btn" onClick={goBackToAllSongs}>
                  ← Volver
                </button>
                <h2>{selectedPlaylist.name}</h2>
              </>
            ) : selectedAlbum ? (
              <>
                <button className="back-btn" onClick={() => setSelectedAlbum(null)}>
                  ← Álbumes
                </button>
                <h2>{selectedAlbum.title}</h2>
              </>
            ) : (
              <div className="view-tabs">
                <button
                  className={`tab-btn ${activeTab === 'albums' ? 'active' : ''}`}
                  onClick={() => setActiveTab('albums')}
                >
                  Álbumes
                </button>
                <button
                  className={`tab-btn ${activeTab === 'songs' ? 'active' : ''}`}
                  onClick={() => setActiveTab('songs')}
                >
                  Canciones
                </button>
              </div>
            )}
          </div>

          {!selectedPlaylist && !selectedAlbum && activeTab === 'albums' ? (
            <div className="album-grid">
              {filteredAlbums.map((album) => (
                <div
                  key={album.id}
                  className="album-card"
                  onClick={() => openAlbum(album)}
                >
                  {album.cover ? (
                    <img
                      src={album.cover}
                      alt={album.title}
                      className="album-card-cover"
                    />
                  ) : (
                    <div className="album-card-cover album-card-placeholder">🎵</div>
                  )}
                  <h3>{album.title}</h3>
                  <p>{album.artist_name}</p>
                  <span className="album-card-count">
                    {album.songs.length} canción{album.songs.length !== 1 ? 'es' : ''}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="songs">
              {(selectedPlaylist
                ? selectedPlaylist.songs
                : selectedAlbum
                ? selectedAlbum.songs
                : filteredSongs
              ).map((song) => (
                <div
                  key={song.id}
                  className={`song-item ${
                    currentSong?.id === song.id ? "active" : ""
                  }`}
                >
                  <div className="song-info" onClick={() => playSong(song)}>
                    {song.album_cover && (
                      <img
                        src={song.album_cover}
                        alt="cover"
                        className="song-cover"
                      />
                    )}
                    <div>
                      <h3>{song.title}</h3>
                      <p>{song.artist_name}</p>
                    </div>
                  </div>

                  <div className="song-actions">
  <button
    className="play-btn"
    onClick={() => {
      if (currentSong?.id === song.id) togglePlayPause();
      else playSong(song);
    }}
  >
    {currentSong?.id === song.id && isPlaying ? "⏸" : "▶"}
  </button>

  {selectedPlaylist ? (
    <button
      className="remove-song-btn"
      onClick={() =>
        removeSongFromPlaylist(
          selectedPlaylist.id,
          song.id,
          song.title
        )
      }
    >
      🗑️
    </button>
  ) : (
    <button
      className="add-song-btn"
      onClick={() => {
        setSelectedSongForPlaylist(song);
        setShowPlaylistMenu(true);
      }}
    >
      + Playlist
    </button>
  )}
</div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* === MODAL === */}
      {showPlaylistMenu && selectedSongForPlaylist && (
        <div className="playlist-modal">
          <div className="playlist-modal-content">
            <h3>Agregar a playlist</h3>
            <p>{selectedSongForPlaylist.title}</p>
            <div className="playlist-modal-buttons">
              {playlists.map((playlist) => (
                <button
                  key={playlist.id}
                  className="playlist-option"
                  onClick={() =>
                    addSongToPlaylist(
                      playlist.id,
                      selectedSongForPlaylist.id,
                      playlist.name
                    )
                  }
                >
                  {playlist.name}
                </button>
              ))}
            </div>
            <button
              className="cancel-button"
              onClick={() => setShowPlaylistMenu(false)}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
      
      {/* === MODAL COMPARTIR === */}
{showShareModal && shareTargetPlaylist && (
  <div className="playlist-modal">
    <div className="playlist-modal-content">
      <h3>Compartir "{shareTargetPlaylist.name}"</h3>
      <p style={{marginBottom:'10px'}}>Ingresa el nombre de usuario:</p>
      <input
        type="text"
        placeholder="username"
        value={shareUsername}
        onChange={(e) => setShareUsername(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && sendShare()}
        style={{width:'100%', padding:'8px', marginBottom:'10px', borderRadius:'4px', border:'1px solid #444', background:'#222', color:'#fff'}}
        autoFocus
      />
      <div className="playlist-modal-buttons">
        <button className="playlist-option" onClick={sendShare}>Compartir</button>
        <button className="cancel-button" onClick={() => setShowShareModal(false)}>Cancelar</button>
      </div>
    </div>
  </div>
)}

     {/* === TOAST === */}
      {toastMessage && <div className="toast">{toastMessage}</div>}

     {/* === BÚSQUEDA MÓVIL === */}
      {showMobileSearch && (
        <div className="mobile-search-overlay">
          <div className="mobile-search-container">
            <div className="mobile-search-header">
              <input
                type="text"
                placeholder="Buscar canciones, artistas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="mobile-search-input"
                autoFocus
              />
              <button
                className="mobile-search-close"
                onClick={() => setShowMobileSearch(false)}
              >
                ✕
              </button>
            </div>
            <div className="mobile-search-results">
              {searchQuery && (
                <p className="search-results-count">
                  {filteredSongs.length} resultado{filteredSongs.length !== 1 ? 's' : ''}
                </p>
              )}
              
              {/* Lista de canciones */}
              <div className="mobile-search-songs">
                {filteredSongs.map((song) => (
                  <div
                    key={song.id}
                    className={`song-item ${
                      currentSong?.id === song.id ? "active" : ""
                    }`}
                  >
                    <div className="song-info" onClick={() => {
                      playSong(song);
                      setShowMobileSearch(false);
                    }}>
                      {song.album_cover && (
                        <img
                          src={song.album_cover}
                          alt="cover"
                          className="song-cover"
                        />
                      )}
                      <div>
                        <h3>{song.title}</h3>
                        <p>{song.artist_name}</p>
                      </div>
                    </div>

                    <div className="song-actions">
                      <button
                        className="play-btn"
                        onClick={() => {
                          if (currentSong?.id === song.id) togglePlayPause();
                          else playSong(song);
                          setShowMobileSearch(false);
                        }}
                      >
                        {currentSong?.id === song.id && isPlaying ? "⏸" : "▶"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
      )}
      </> //este es el cierre del condicional de arriba del primer return, donde se puso la autentificacion
   );
}

export default App;
